import React, { useState, useEffect, useMemo } from 'react';
import ReactGA from 'react-ga4';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faGear } from '@fortawesome/free-solid-svg-icons';
import './CheckCoverage.css';

const API_URL = process.env.REACT_APP_INFLEXXIONHEALTH_API_URL;
const SUPPORT_EMAIL = process.env.REACT_APP_INFLEXXIONHEALTH_SUPPORT_EMAIL;
const SELF_PAY_PRICE = '$150';
// Contracted in-network rate shown on the deductible/coinsurance screens.
// Until real contract rates are configured the copy degrades to wording
// without a dollar amount.
const IN_NETWORK_RATE = parseFloat(process.env.REACT_APP_IN_NETWORK_VISIT_RATE);

// States Inflexxion Health currently serves (mirrors stateInfo in ScheduleScreen).
const SERVED_STATES = ['AZ', 'CO', 'ID', 'UT', 'WY'];

const US_STATES = [
  ['AL', 'Alabama'], ['AK', 'Alaska'], ['AZ', 'Arizona'], ['AR', 'Arkansas'],
  ['CA', 'California'], ['CO', 'Colorado'], ['CT', 'Connecticut'], ['DE', 'Delaware'],
  ['DC', 'District of Columbia'], ['FL', 'Florida'], ['GA', 'Georgia'], ['HI', 'Hawaii'],
  ['ID', 'Idaho'], ['IL', 'Illinois'], ['IN', 'Indiana'], ['IA', 'Iowa'],
  ['KS', 'Kansas'], ['KY', 'Kentucky'], ['LA', 'Louisiana'], ['ME', 'Maine'],
  ['MD', 'Maryland'], ['MA', 'Massachusetts'], ['MI', 'Michigan'], ['MN', 'Minnesota'],
  ['MS', 'Mississippi'], ['MO', 'Missouri'], ['MT', 'Montana'], ['NE', 'Nebraska'],
  ['NV', 'Nevada'], ['NH', 'New Hampshire'], ['NJ', 'New Jersey'], ['NM', 'New Mexico'],
  ['NY', 'New York'], ['NC', 'North Carolina'], ['ND', 'North Dakota'], ['OH', 'Ohio'],
  ['OK', 'Oklahoma'], ['OR', 'Oregon'], ['PA', 'Pennsylvania'], ['RI', 'Rhode Island'],
  ['SC', 'South Carolina'], ['SD', 'South Dakota'], ['TN', 'Tennessee'], ['TX', 'Texas'],
  ['UT', 'Utah'], ['VT', 'Vermont'], ['VA', 'Virginia'], ['WA', 'Washington'],
  ['WV', 'West Virginia'], ['WI', 'Wisconsin'], ['WY', 'Wyoming'],
];

const NOT_LISTED = '__not_listed__';

const SCREENS = {
  SELECT: 'select',
  NO_STATE: 'no_state',
  NOT_ACCEPTED: 'not_accepted',
  IN_NETWORK: 'in_network',
  CHECKING: 'checking',
  RESULT: 'result',
};

// Dev/demo shortcut: /check-coverage?preview=<name> jumps straight to a
// screen with canned data, so every branch can be reviewed without a real
// member or API call. Disabled in production builds.
const PREVIEW_RESULTS = {
  copay: { coverage: 'active', payer_name: 'Aetna (CVS Health)', plan_name: 'Open Access', copay: '30' },
  deductible: {
    coverage: 'active',
    payer_name: 'Aetna (CVS Health)',
    plan_name: 'Open Access',
    deductible_total: '1500',
    deductible_remaining: '750',
  },
  coinsurance: {
    coverage: 'active',
    payer_name: 'Aetna (CVS Health)',
    plan_name: 'Open Access',
    coinsurance_percent: '20',
  },
  covered: { coverage: 'active', payer_name: 'Aetna (CVS Health)' },
  'not-covered': { coverage: 'not_covered', payer_name: 'Aetna (CVS Health)' },
  'no-active': { coverage: 'inactive', payer_name: 'Aetna (CVS Health)' },
};

const track = (action, label) => {
  try {
    if (ReactGA.isInitialized) {
      ReactGA.event({ category: 'Coverage Check', action, label });
    }
  } catch (err) {
    // analytics must never break the flow
  }
};

const isValidEmail = (value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());

const CheckCoverage = () => {
  const [screen, setScreen] = useState(SCREENS.SELECT);

  // Screen 1 selections
  const [stateCode, setStateCode] = useState('');
  const [carrierKey, setCarrierKey] = useState('');
  const [selectError, setSelectError] = useState('');

  // Accepted payers from the API
  const [payers, setPayers] = useState([]);
  const [payersError, setPayersError] = useState(false);

  // Member-detail form (in-network path)
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [dob, setDob] = useState('');
  const [memberId, setMemberId] = useState('');
  const [formError, setFormError] = useState('');
  const [result, setResult] = useState(null);

  // Interest capture: the inquiry row (state + carrier) is recorded the
  // moment a capture branch is reached; this token lets the waitlist form
  // attach name/email to that same row.
  const [interestToken, setInterestToken] = useState(null);

  // Synthetic carrier for ?preview= screens that need one selected.
  const [previewCarrier, setPreviewCarrier] = useState(null);

  // Email-capture forms (newsletter / let-me-know)
  const [notifyOpen, setNotifyOpen] = useState(false);
  const [contactFirst, setContactFirst] = useState('');
  const [contactLast, setContactLast] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [contactError, setContactError] = useState('');
  const [contactSubmitting, setContactSubmitting] = useState(false);
  const [contactDone, setContactDone] = useState(false);

  useEffect(() => {
    const previousTitle = document.title;
    document.title = 'Check Your Coverage — Inflexxion Health';
    return () => {
      document.title = previousTitle;
    };
  }, []);

  useEffect(() => {
    const loadPayers = async () => {
      try {
        const res = await fetch(`${API_URL}/insurance-payers`, { mode: 'cors' });
        const data = await res.json();
        if (data?.status === 'success' && Array.isArray(data.data?.payers)) {
          setPayers(data.data.payers);
          setPayersError(false);
        } else {
          setPayersError(true);
        }
      } catch (err) {
        console.error('Error loading insurance payers:', err);
        setPayersError(true);
      }
    };
    loadPayers();
  }, []);

  // Apply the ?preview= flag once on mount (never in production).
  useEffect(() => {
    if (process.env.REACT_APP_ENVIRONMENT === 'production') return;
    const preview = (new URLSearchParams(window.location.search).get('preview') || '').toLowerCase();
    if (!preview) return;

    if (PREVIEW_RESULTS[preview]) {
      setStateCode('CO');
      setResult(PREVIEW_RESULTS[preview]);
      setScreen(SCREENS.RESULT);
    } else if (preview === 'no-state') {
      setStateCode('MT');
      setPreviewCarrier({ key: 'preview', name: 'Humana', accepted: false });
      setScreen(SCREENS.NO_STATE);
    } else if (preview === 'not-accepted') {
      setStateCode('CO');
      setPreviewCarrier({ key: 'preview', name: 'Humana', accepted: false });
      setScreen(SCREENS.NOT_ACCEPTED);
    } else if (preview === 'in-network') {
      setStateCode('CO');
      setPreviewCarrier({
        key: 'preview',
        name: 'Aetna (CVS Health)',
        accepted: true,
        supportsEligibility: true,
        payerId: '60054',
      });
      setScreen(SCREENS.IN_NETWORK);
    } else if (preview === 'no-elig') {
      setStateCode('UT');
      setPreviewCarrier({
        key: 'preview',
        name: 'University of Utah Health Plans (U of U Health)',
        accepted: true,
        supportsEligibility: false,
        payerId: null,
      });
      setScreen(SCREENS.IN_NETWORK);
    } else if (preview === 'checking') {
      setScreen(SCREENS.CHECKING);
    }
  }, []);

  const stateName = (US_STATES.find(([code]) => code === stateCode) || [])[1] || '';

  // Carriers come from the accepted_insurance_companies table (via the API),
  // one row per state. The dropdown shows only the selected state's carriers;
  // a state with no active carriers keeps the dropdown hidden and Continue
  // routes straight to the "not in your state yet" screen.
  const carrierOptions = useMemo(() => {
    const rows = payers.filter((p) => p.state === stateName);
    if (rows.length === 0) return [];

    const options = rows
      .map((p) => ({
        key: String(p.id),
        name: p.name,
        accepted: true,
        supportsEligibility: p.supports_eligibility === true,
        payerId: p.payer_id || null,
      }))
      .sort((a, b) => a.name.localeCompare(b.name));
    options.push({ key: NOT_LISTED, name: "My insurance isn't listed", accepted: false });
    return options;
  }, [payers, stateName]);

  const stateHasCarriers = carrierOptions.length > 0;
  const showCarrierSelect = Boolean(stateCode) && stateHasCarriers;

  const selectedCarrier = previewCarrier || carrierOptions.find((c) => c.key === carrierKey);
  // Every booking CTA funnels through the marketing site's booking flow.
  const bookingPath = 'https://inflexxionhealth.com/book-your-visit-new#step-1';

  const resetContactForm = () => {
    setNotifyOpen(false);
    setContactFirst('');
    setContactLast('');
    setContactEmail('');
    setContactError('');
    setContactDone(false);
    setInterestToken(null);
  };

  // Phase 1 of the capture: record the anonymous state/carrier inquiry and
  // return the claim token for the (optional) waitlist signup. Never blocks
  // or breaks the page.
  const postInquiry = async (interestType, inquiryState, carrierName) => {
    try {
      const res = await fetch(`${API_URL}/coverage-interest`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        mode: 'cors',
        body: JSON.stringify({
          interest: interestType,
          state: inquiryState,
          insurance_company: carrierName,
        }),
      });
      const data = await res.json();
      if (data?.status === 'success' && data.data?.token) {
        setInterestToken(data.data.token);
        return data.data.token;
      }
    } catch (err) {
      console.error('Error recording coverage interest:', err);
    }
    return null;
  };

  const startOver = () => {
    setScreen(SCREENS.SELECT);
    setSelectError('');
    setFormError('');
    setResult(null);
    setPreviewCarrier(null);
    resetContactForm();
  };

  const handleSelectContinue = (e) => {
    e.preventDefault();
    setSelectError('');
    if (!stateCode) {
      setSelectError('Please select your state');
      return;
    }
    if (stateHasCarriers && !carrierKey) {
      setSelectError('Please select your insurance carrier');
      return;
    }
    const carrierName = !stateHasCarriers
      ? ''
      : carrierKey === NOT_LISTED
        ? 'Not listed'
        : selectedCarrier
          ? selectedCarrier.name
          : carrierKey;
    track('carrier_selected', `${stateCode} / ${carrierName || 'none'}`);

    resetContactForm();
    // No active carriers for this state, or a state we don't serve → the
    // "not in your state yet" screen (the inquiry is still captured).
    if (!stateHasCarriers || !SERVED_STATES.includes(stateCode)) {
      track('state_not_served', stateCode);
      postInquiry('state_waitlist', stateName, carrierName);
      setScreen(SCREENS.NO_STATE);
      return;
    }
    if (!selectedCarrier || !selectedCarrier.accepted) {
      track('carrier_not_accepted', carrierName);
      postInquiry('network_waitlist', stateName, carrierName);
      setScreen(SCREENS.NOT_ACCEPTED);
      return;
    }
    setFormError('');
    setScreen(SCREENS.IN_NETWORK);
  };

  const submitContact = async (e, interestType) => {
    e.preventDefault();
    setContactError('');
    if (!contactFirst.trim() || !contactLast.trim() || !contactEmail.trim()) {
      setContactError('Please fill in all fields.');
      return;
    }
    if (!isValidEmail(contactEmail)) {
      setContactError('Please enter a valid email address.');
      return;
    }
    setContactSubmitting(true);
    try {
      // Phase 2: attach the contact details to the inquiry row recorded when
      // this screen was reached. If that insert failed (or hasn't landed
      // yet), record the inquiry now and use its token.
      let token = interestToken;
      if (!token) {
        const carrierName =
          carrierKey === NOT_LISTED ? 'Not listed' : selectedCarrier ? selectedCarrier.name : '';
        token = await postInquiry(interestType, stateName, carrierName);
      }
      if (!token) throw new Error('no inquiry token');

      const res = await fetch(`${API_URL}/coverage-interest`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        mode: 'cors',
        body: JSON.stringify({
          token,
          first_name: contactFirst.trim(),
          last_name: contactLast.trim(),
          email: contactEmail.trim(),
        }),
      });
      const data = await res.json();
      if (data?.status !== 'success') throw new Error(data?.message || 'save failed');
      track('interest_submitted', interestType);
      setContactDone(true);
    } catch (err) {
      console.error('Error saving contact details:', err);
      setContactError(
        `We couldn't save your details right now. Email us at ${SUPPORT_EMAIL} and we'll keep you posted.`
      );
    } finally {
      setContactSubmitting(false);
    }
  };

  const handleEligibilitySubmit = async (e) => {
    e.preventDefault();
    setFormError('');
    if (!firstName.trim() || !lastName.trim() || !dob || !memberId.trim()) {
      setFormError('Please fill in all fields.');
      return;
    }
    setScreen(SCREENS.CHECKING);
    try {
      const res = await fetch(`${API_URL}/check-insurance-eligibility`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        mode: 'cors',
        body: JSON.stringify({
          first_name: firstName.trim(),
          last_name: lastName.trim(),
          dob,
          payer_id: selectedCarrier.payerId,
          member_id: memberId.trim(),
        }),
      });
      const data = await res.json();
      if (data?.status === 'success') {
        setResult(data.data);
        track('check_result', data.data?.coverage || 'unknown');
        setScreen(SCREENS.RESULT);
      } else {
        setFormError(
          data?.message || "We couldn't verify your coverage right now. Please try again later."
        );
        setScreen(SCREENS.IN_NETWORK);
      }
    } catch (err) {
      console.error('Eligibility check error:', err);
      setFormError("We couldn't verify your coverage right now. Please try again later.");
      setScreen(SCREENS.IN_NETWORK);
    }
  };

  const recheckInsurance = () => {
    setFirstName('');
    setLastName('');
    setDob('');
    setMemberId('');
    setFormError('');
    setResult(null);
    setScreen(SCREENS.IN_NETWORK);
  };

  const startOverLink = (
    <button type="button" className="cc-back-link" onClick={startOver}>
      &larr; Change state or insurance
    </button>
  );

  const contactFields = (
    <>
      <div className="cc-field-row">
        <div className="cc-field">
          <label htmlFor="cc-contact-first">First name</label>
          <input
            id="cc-contact-first"
            type="text"
            autoComplete="given-name"
            value={contactFirst}
            onChange={(e) => setContactFirst(e.target.value)}
            required
          />
        </div>
        <div className="cc-field">
          <label htmlFor="cc-contact-last">Last name</label>
          <input
            id="cc-contact-last"
            type="text"
            autoComplete="family-name"
            value={contactLast}
            onChange={(e) => setContactLast(e.target.value)}
            required
          />
        </div>
      </div>
      <div className="cc-field">
        <label htmlFor="cc-contact-email">Email address</label>
        <input
          id="cc-contact-email"
          type="email"
          autoComplete="email"
          value={contactEmail}
          onChange={(e) => setContactEmail(e.target.value)}
          required
        />
      </div>
    </>
  );

  const costDisclaimer = (
    <p className="cc-disclaimer">
      This is an estimate based on information returned by your insurance company. It is not a
      guarantee of coverage or final cost. We will confirm your coverage with you before your
      consultation.
    </p>
  );

  const renderResult = () => {
    if (!result) return null;

    if (result.coverage === 'active') {
      const copay = parseFloat(result.copay);
      const deductibleRemaining = parseFloat(result.deductible_remaining);
      const coinsurance = parseFloat(result.coinsurance_percent);
      const hasRate = !Number.isNaN(IN_NETWORK_RATE) && IN_NETWORK_RATE > 0;

      if (copay > 0) {
        return (
          <div className="cc-result">
            <h2 className="cc-result-heading">You're Covered</h2>
            <p className="cc-sub">Use your insurance for your consultations</p>
            <p>
              Your plan is active and in-network with Inflexxion Health. Your co-pay for each video
              visit is ${result.copay}, and you'll pay it after your consultation occurs.
            </p>
            <a href={bookingPath} className="cc-btn cc-btn-book">Book my visit</a>
            {costDisclaimer}
          </div>
        );
      }

      if (deductibleRemaining > 0) {
        return (
          <div className="cc-result">
            <h2 className="cc-result-heading">You're covered, and your visit counts toward your deductible</h2>
            <p>
              Your plan is active and in-network with Inflexxion Health. Since you haven't met your
              deductible yet, you'll pay our in-network rate
              {hasRate ? (
                <>: ${IN_NETWORK_RATE} for your first video visit</>
              ) : (
                <> for your first video visit</>
              )}
              . Every visit counts toward your deductible, and you can pay with an HSA or FSA card.
            </p>
            <a href={bookingPath} className="cc-btn cc-btn-book">Book my visit</a>
            {costDisclaimer}
          </div>
        );
      }

      if (coinsurance > 0) {
        const coinsuranceAmount = hasRate ? Math.round((coinsurance / 100) * IN_NETWORK_RATE) : null;
        return (
          <div className="cc-result">
            <h2 className="cc-result-heading">You're covered, and you've met your deductible</h2>
            <p>
              Your plan is active and in-network with Inflexxion Health. Since you've met your
              deductible, your insurance now pays most of the cost. Your share, called coinsurance,
              is{' '}
              {coinsuranceAmount !== null ? (
                <>${coinsuranceAmount}</>
              ) : (
                <>{result.coinsurance_percent}% of our in-network rate</>
              )}{' '}
              for your first video visit and you can pay with an HSA or FSA card.
            </p>
            <a href={bookingPath} className="cc-btn cc-btn-book">Book my visit</a>
            {costDisclaimer}
          </div>
        );
      }

      return (
        <div className="cc-result">
          <h2 className="cc-result-heading">You're Covered</h2>
          <p className="cc-sub">Use your insurance for your consultations</p>
          <p>
            Your plan is active and in-network with Inflexxion Health. Your insurance company
            didn't share your exact cost electronically, so we'll confirm your out-of-pocket cost
            with you before your visit.
          </p>
          <a href={bookingPath} className="cc-btn cc-btn-book">Book my visit</a>
        </div>
      );
    }

    if (result.coverage === 'not_covered') {
      return (
        <div className="cc-result">
          <h2 className="cc-result-heading">Your plan is active, but this visit may not be covered</h2>
          <p>
            {result.payer_name} reports your plan is active, but video consultations like ours may
            not be a covered benefit on your plan. You can still see one of our clinicians with a
            self-pay visit: your first video consultation is {SELF_PAY_PRICE}, you can pay with an
            HSA or FSA, and we'll give you a superbill to submit to your insurer for possible
            reimbursement.
          </p>
          <div className="cc-cta-row">
            <button type="button" className="cc-btn cc-btn-secondary" onClick={recheckInsurance}>
              Re-check insurance
            </button>
            <a href={bookingPath} className="cc-btn cc-btn-book">Book a self-pay visit</a>
          </div>
        </div>
      );
    }

    // inactive / unknown → spec's "verification unsuccessful" screen
    return (
      <div className="cc-result cc-result-left">
        <h2 className="cc-result-heading">We couldn't confirm active coverage for your plan</h2>
        <p>
          We work with your insurance company, but this plan doesn't show as active right now. This
          can happen if your coverage recently changed or if a detail was entered differently from
          your card.
        </p>
        <p className="cc-list-intro">Here's what you can do:</p>
        <ul className="cc-help-list">
          <li>
            <strong>Double-check your details.</strong> Make sure your member ID and name match
            your insurance card exactly.
          </li>
          <li>
            <strong>Check with your insurer.</strong> Call the number on the back of your card to
            confirm your coverage.
          </li>
          <li>
            <strong>Book a self-pay visit now.</strong> Your first video consultation is{' '}
            {SELF_PAY_PRICE}, and you can pay with your HSA or FSA. We'll also give you a superbill
            to submit to your insurer for possible reimbursement.
          </li>
        </ul>
        <div className="cc-cta-row">
          <button type="button" className="cc-btn cc-btn-secondary" onClick={recheckInsurance}>
            Re-check insurance
          </button>
          <a href={bookingPath} className="cc-btn cc-btn-book">Book a self-pay visit</a>
        </div>
      </div>
    );
  };

  return (
    <div className="cc-page">
      <div className="cc-card">
        {screen === SCREENS.SELECT && (
          <form onSubmit={handleSelectContinue} noValidate>
            <h1 className="cc-heading">Check your coverage for consultations</h1>
            <p className="cc-sub">Select your state and insurance carrier</p>

            <div className="cc-field">
              <label htmlFor="cc-state">State</label>
              <select
                id="cc-state"
                value={stateCode}
                onChange={(e) => {
                  setStateCode(e.target.value);
                  // The carrier list is per-state; a previous pick may not exist here.
                  setCarrierKey('');
                }}
                required
              >
                <option value="" disabled>Select your state</option>
                {US_STATES.map(([code, name]) => (
                  <option key={code} value={code}>{name}</option>
                ))}
              </select>
            </div>

            {showCarrierSelect && (
              <div className="cc-field">
                <label htmlFor="cc-carrier">Insurance carrier</label>
                <select
                  id="cc-carrier"
                  value={carrierKey}
                  onChange={(e) => setCarrierKey(e.target.value)}
                  required
                >
                  <option value="" disabled>Select your insurance carrier</option>
                  {carrierOptions.map((c) => (
                    <option key={c.key} value={c.key}>{c.name}</option>
                  ))}
                </select>
              </div>
            )}

            {showCarrierSelect && (
              <p className="cc-footnote">
                * Don't see your insurance company here? Email{' '}
                <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a> and we will help.
              </p>
            )}

            {payersError && (
              <p className="cc-form-error" role="alert">
                We couldn't load the insurance carrier list right now. Please try again later, or
                email <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a> and we'll check your
                coverage for you.
              </p>
            )}

            {selectError && (
              <p className="cc-form-error" role="alert">{selectError}</p>
            )}

            <div className="cc-actions">
              <button type="submit" className="cc-btn cc-btn-primary" disabled={payersError}>
                Continue
              </button>
            </div>
          </form>
        )}

        {screen === SCREENS.NO_STATE && (
          <div>
            {startOverLink}
            <h1 className="cc-heading">We're not in your state yet</h1>
            <p className="cc-sub">
              Join our newsletter for evidence-based menopause guidance and updates as Inflexxion
              Health grows
            </p>

            {contactDone ? (
              <div className="cc-result">
                <p>
                  Thanks — you're on the list. We'll let you know as soon as Inflexxion Health
                  reaches your state.
                </p>
                <a href="https://inflexxionhealth.com" className="cc-btn cc-btn-primary">
                  Back to inflexxionhealth.com
                </a>
              </div>
            ) : (
              <form onSubmit={(e) => submitContact(e, 'state_waitlist')} noValidate>
                {contactFields}
                {contactError && (
                  <p className="cc-form-error" role="alert">{contactError}</p>
                )}
                <div className="cc-actions">
                  <button type="submit" className="cc-btn cc-btn-primary" disabled={contactSubmitting}>
                    {contactSubmitting ? 'Joining…' : 'Join the newsletter'}
                  </button>
                </div>
              </form>
            )}
          </div>
        )}

        {screen === SCREENS.NOT_ACCEPTED && (
          <div>
            {startOverLink}
            <h1 className="cc-heading">We're not in-network with your insurance yet</h1>
            <p className="cc-sub">
              You can still see one of our clinicians with a self-pay visit. Your first video
              consultation is {SELF_PAY_PRICE}, and you can pay with your HSA or FSA. We'll also
              give you a superbill, an itemized receipt you can submit to your insurer for possible
              out-of-network reimbursement.
            </p>

            {contactDone ? (
              <div className="cc-result">
                <p>Thanks — we'll email you as soon as we're in-network with your insurance.</p>
                <div className="cc-cta-row">
                  <a href={bookingPath} className="cc-btn cc-btn-book">Book your first visit</a>
                  <a href="https://inflexxionhealth.com" className="cc-btn cc-btn-primary">Continue</a>
                </div>
              </div>
            ) : (
              <>
                <div className="cc-cta-row">
                  <a href={bookingPath} className="cc-btn cc-btn-book">Book your first visit</a>
                  <button
                    type="button"
                    className="cc-btn cc-btn-secondary"
                    aria-expanded={notifyOpen}
                    onClick={() => setNotifyOpen((open) => !open)}
                  >
                    Let me know when I'm covered
                  </button>
                </div>

                {notifyOpen && (
                  <form className="cc-notify-form" onSubmit={(e) => submitContact(e, 'network_waitlist')} noValidate>
                    {contactFields}
                    <p className="cc-consent">
                      By entering your email, you agree to receive marketing materials from
                      Inflexxion Health.
                    </p>
                    {contactError && (
                      <p className="cc-form-error" role="alert">{contactError}</p>
                    )}
                    <div className="cc-actions">
                      <button type="submit" className="cc-btn cc-btn-primary" disabled={contactSubmitting}>
                        {contactSubmitting ? 'Saving…' : 'Continue'}
                      </button>
                    </div>
                  </form>
                )}
              </>
            )}
          </div>
        )}

        {screen === SCREENS.IN_NETWORK && (
          <div>
            {startOverLink}
            <h1 className="cc-heading">Good news: we're in-network with your insurance</h1>

            {selectedCarrier && !selectedCarrier.supportsEligibility ? (
              <>
                <p className="cc-sub">
                  We can't verify {selectedCarrier.name} plans electronically at this moment. Email
                  us at <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a> and we'll confirm
                  your coverage for you — or book now and we'll verify it before your visit.
                </p>
                <div className="cc-actions">
                  <a href={bookingPath} className="cc-btn cc-btn-book">Book my visit</a>
                </div>
              </>
            ) : (
              <>
                <p className="cc-sub">
                  We just need a few more details to confirm your specific plan is covered. Enter
                  them exactly as they appear on your insurance card.
                </p>
                <form onSubmit={handleEligibilitySubmit} noValidate>
                  <div className="cc-field-row">
                    <div className="cc-field">
                      <label htmlFor="cc-first-name">First name</label>
                      <input
                        id="cc-first-name"
                        type="text"
                        autoComplete="given-name"
                        value={firstName}
                        onChange={(e) => setFirstName(e.target.value)}
                        required
                      />
                    </div>
                    <div className="cc-field">
                      <label htmlFor="cc-last-name">Last name</label>
                      <input
                        id="cc-last-name"
                        type="text"
                        autoComplete="family-name"
                        value={lastName}
                        onChange={(e) => setLastName(e.target.value)}
                        required
                      />
                    </div>
                  </div>

                  <div className="cc-field">
                    <label htmlFor="cc-dob">Date of birth</label>
                    <input
                      id="cc-dob"
                      type="date"
                      autoComplete="bday"
                      max={new Date().toISOString().split('T')[0]}
                      value={dob}
                      onChange={(e) => setDob(e.target.value)}
                      required
                    />
                  </div>

                  <div className="cc-field">
                    <label htmlFor="cc-member-id">Member ID</label>
                    <input
                      id="cc-member-id"
                      type="text"
                      autoComplete="off"
                      value={memberId}
                      onChange={(e) => setMemberId(e.target.value)}
                      required
                    />
                  </div>

                  {formError && (
                    <p className="cc-form-error" role="alert">{formError}</p>
                  )}

                  <div className="cc-actions">
                    <button type="submit" className="cc-btn cc-btn-primary">
                      Check your coverage
                    </button>
                  </div>
                </form>
              </>
            )}
          </div>
        )}

        {screen === SCREENS.CHECKING && (
          <div className="cc-checking" role="status" aria-live="polite">
            <FontAwesomeIcon icon={faGear} className="fa-spin cc-checking-icon" aria-hidden="true" />
            <p>Checking with your insurance company...</p>
            <p className="cc-checking-sub">This usually takes a few seconds.</p>
          </div>
        )}

        {screen === SCREENS.RESULT && (
          <div>
            {startOverLink}
            {renderResult()}
          </div>
        )}

        {screen !== SCREENS.CHECKING && (
          <p className="cc-help-text">
            Questions? Email <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>
          </p>
        )}
      </div>
    </div>
  );
};

export default CheckCoverage;
